import { axiTokens } from '@axi/tokens/ts';
import type { axiTokens as AxiTokensType } from '@axi/tokens/ts';

export type CssVarMap = Record<string, string>;
export type AxiTokens = typeof AxiTokensType;

const kebab = (input: string): string =>
  input.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`).replace(/^-/, '');

function flatten(
  obj: Record<string, unknown>,
  prefix: string
): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [key, value] of Object.entries(obj)) {
    const nextPrefix = prefix ? `${prefix}-${kebab(key)}` : kebab(key);
    if (typeof value === 'string') {
      out.push([`--${nextPrefix}`, value]);
    } else if (value && typeof value === 'object') {
      out.push(...flatten(value as Record<string, unknown>, nextPrefix));
    }
  }
  return out;
}

/**
 * Convert `axiTokens` into a flat CSS variables map.
 *
 * Prefer the provider's pre-normalized `cssVariables.root` when present.
 * Fall back to deriving CSS vars from the flat keys (e.g. `sidebarWidth` →
 * `--sidebar-width`) so the consumer survives minor token shape drift.
 *
 * The function is pure: no DOM access, no React imports, no side effects.
 */
export function tokensToCssVars(tokens: AxiTokens = axiTokens): CssVarMap {
  const preNormalized = (tokens as { cssVariables?: { root?: CssVarMap } })
    .cssVariables?.root;
  if (preNormalized && Object.keys(preNormalized).length > 0) {
    return { ...preNormalized };
  }
  const skipKey = 'cssVariables';
  const out: CssVarMap = {};
  for (const [k, v] of Object.entries(tokens)) {
    if (k === skipKey) continue;
    if (v && typeof v === 'object') {
      for (const [varName, value] of flatten(v as Record<string, unknown>, k)) {
        out[varName] = value;
      }
    } else if (typeof v === 'string') {
      out[`--${kebab(k)}`] = v;
    }
  }
  return out;
}

export function cssVarsToRule(vars: CssVarMap): string {
  const body = Object.entries(vars)
    .map(([k, v]) => `${k}:${v};`)
    .join(' ');
  return `:root{${body}}`;
}

/**
 * Merge CSS variable maps with overrides winning.
 *
 * Pure function. Order: base keys first, then override keys; the override
 * values overwrite the base values at any matching key. The returned map
 * preserves override insertion order at the tail, which matters only for
 * human inspection of the rendered CSS — the browser treats all matching
 * `--*` declarations identically.
 */
export function tokensWithOverrides(
  base: CssVarMap,
  overrides: CssVarMap
): CssVarMap {
  return { ...base, ...overrides };
}